import React, { useEffect, useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { api } from '../api';

const CONDITION_COLORS = {
  New: 'bg-emerald-600 text-white', Good: 'bg-blue-600 text-white', Fair: 'bg-amber-500 text-white',
  Poor: 'bg-orange-600 text-white', Damaged: 'bg-rose-600 text-white',
};

const STEPS = [
  { id: 'property', label: 'Property', icon: 'MapPin' },
  { id: 'rooms', label: 'Rooms', icon: 'Home' },
  { id: 'record', label: 'Record', icon: 'PencilLine' },
  { id: 'review', label: 'Review', icon: 'CheckCircle2' },
];

// Voice-to-text mic button — records audio and transcribes via backend Whisper (works on iOS/Android).
const MicButton = ({ onText }) => {
  const [state, setState] = useState('idle'); // idle | recording | processing
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const { toast } = useToast();

  const stop = () => { if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop(); };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        const ext = (rec.mimeType || '').includes('mp4') ? 'mp4' : 'webm';
        setState('processing');
        try {
          const fd = new FormData();
          fd.append('audio', blob, `dictation.${ext}`);
          const r = await api.post('/ai/transcribe', fd);
          const text = (r.data && r.data.text) || '';
          if (text) onText(text);
          else toast({ title: "Didn't catch that", description: 'Please try dictating again.' });
        } catch (e) { toast({ title: 'Transcription failed', description: 'Please try again.' }); }
        finally { setState('idle'); }
      };
      recRef.current = rec; rec.start(); setState('recording');
    } catch (e) {
      toast({ title: 'Microphone blocked', description: 'Allow microphone access to dictate.' });
    }
  };

  const toggle = () => { if (state === 'recording') stop(); else if (state === 'idle') start(); };

  return (
    <button type="button" onClick={toggle} disabled={state === 'processing'}
      title={state === 'recording' ? 'Tap to stop' : 'Tap to dictate'}
      className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center border transition-colors ${state === 'recording' ? 'bg-rose-500 text-white border-rose-500 animate-pulse' : 'border-slate-300 text-slate-600 hover:border-[#1c63c4]'} ${state === 'processing' ? 'opacity-60' : ''}`}>
      {state === 'processing' ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Mic className="h-4 w-4" />}
    </button>
  );
};

const InventoryRecorder = ({ job, onClose, onCompleted }) => {
  const { toast } = useToast();
  const [tpl, setTpl] = useState(null);
  const [step, setStep] = useState('property');
  const [room, setRoom] = useState('');
  const [data, setData] = useState({});
  const [meta, setMeta] = useState({ inspector: '', date: job.scheduled_date || '', tenant: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [identifying, setIdentifying] = useState(null);

  useEffect(() => { api.get('/inventory/template').then((r) => { setTpl(r.data); setRoom(r.data.rooms[0].id); }); }, []);

  const roomObj = tpl?.rooms.find((r) => r.id === room);
  const getItem = (item) => data[`${room}|${item}`] || {};
  const setItem = (item, patch) => setData((d) => ({ ...d, [`${room}|${item}`]: { ...d[`${room}|${item}`], ...patch } }));
  const setMetaField = (k, v) => setMeta((m) => ({ ...m, [k]: v }));

  const recordedIn = (r) => r.items.filter((it) => (data[`${r.id}|${it}`] || {}).condition).length;
  const roomRecorded = roomObj ? recordedIn(roomObj) : 0;
  const totalRecorded = Object.values(data).filter((v) => v.condition).length;
  const totalItems = tpl ? tpl.rooms.reduce((a, r) => a + r.items.length, 0) : 0;

  const onPhoto = async (item, file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      const cur = getItem(item);
      setItem(item, { photos: [...(cur.photos || []), dataUrl] });
      setIdentifying(item);
      try {
        const r = await api.post('/ai/identify', { image: dataUrl });
        const note = r.data.note || '';
        const desc = r.data.item || '';
        setItem(item, { description: getItem(item).description || desc, notes: getItem(item).notes || note });
        if (desc) toast({ title: 'AI identified', description: `${desc}${note ? ' — ' + note : ''}` });
      } catch (e) { /* ignore */ } finally { setIdentifying(null); }
    };
    reader.readAsDataURL(file);
  };

  const buildSummary = () => {
    const lines = [];
    if (meta.inspector) lines.push(`Inspector: ${meta.inspector}`);
    if (meta.date) lines.push(`Inspection date: ${meta.date}`);
    if (meta.tenant) lines.push(`Tenant: ${meta.tenant}`);
    if (meta.notes) lines.push(`Property notes: ${meta.notes}`);
    if (lines.length) lines.push('');
    tpl.rooms.forEach((r) => {
      r.items.forEach((it) => {
        const v = data[`${r.id}|${it}`];
        if (v && v.condition) lines.push(`${r.name} – ${it}: ${v.condition}${v.description ? ' (' + v.description + ')' : ''}${v.notes ? '. ' + v.notes : ''}`);
      });
    });
    return lines.join('\n');
  };

  const submit = async () => {
    setBusy(true);
    try {
      const rooms = tpl.rooms.map((r) => ({ id: r.id, name: r.name, items: r.items.map((it) => ({ name: it, ...(data[`${r.id}|${it}`] || {}) })).filter((i) => i.condition || (i.photos && i.photos.length) || i.notes || i.description) })).filter((r) => r.items.length);
      await api.post(`/jobs/${job.id}/inventory`, { rooms });
      await api.post(`/jobs/${job.id}/complete`, { summary: buildSummary() });
      toast({ title: 'Inspection submitted', description: 'Report & invoice generated.' });
      onCompleted && onCompleted();
    } catch (e) { toast({ title: 'Submit failed', description: e?.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };

  if (!tpl) return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center">
      <div className="p-8 text-center text-white flex items-center gap-2"><Icons.Loader2 className="h-5 w-5 animate-spin" /> Loading recorder…</div>
    </div>
  );
  const descOptions = (item) => tpl.descPresets[item] || [];

  const gotoRoom = (id) => { setRoom(id); setStep('record'); };

  const Field = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-2 border-b border-border last:border-0"><span className="text-sm text-muted-foreground">{label}</span><span className="text-sm font-medium text-[#102d55] text-right">{value || '—'}</span></div>
  );

  const renderItemCard = (item) => {
    const v = getItem(item);
    const keysLike = roomObj.keys;
    return (
      <div key={item} data-testid={`item-card-${item}`} className="bg-white rounded-xl border border-border p-4">
        <div className="flex items-center justify-between mb-3"><div className="font-display font-semibold text-[#102d55]">{item}</div><div className="text-xs text-muted-foreground">{v.condition || 'Not recorded'}</div></div>
        {!keysLike && (
          <div className="flex flex-wrap gap-2 mb-3">
            {tpl.conditions.map((c) => (
              <button key={c} onClick={() => setItem(item, { condition: c })} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${v.condition === c ? CONDITION_COLORS[c] + ' border-transparent' : 'bg-white text-slate-600 border-slate-300 hover:border-[#1c63c4]'}`}>{c}</button>
            ))}
          </div>
        )}
        {descOptions(item).length > 0 && (
          <Select value={v.description || ''} onValueChange={(val) => setItem(item, { description: val })}>
            <SelectTrigger className="mb-2 h-9 text-sm"><SelectValue placeholder="Choose description preset…" /></SelectTrigger>
            <SelectContent>{descOptions(item).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <div className="flex gap-2 items-start mb-3">
          <Textarea value={v.description || ''} onChange={(e) => setItem(item, { description: e.target.value })} rows={2} placeholder={keysLike ? 'Key location & description…' : 'Enter item description…'} className="text-sm" />
          <MicButton onText={(t) => setItem(item, { description: ((getItem(item).description || '') + ' ' + t).trim() })} />
        </div>
        {!keysLike && (
          <>
            <Select value={v.notes_preset || ''} onValueChange={(val) => setItem(item, { notes: ((v.notes ? v.notes + '. ' : '') + val), notes_preset: val })}>
              <SelectTrigger className="mb-2 h-9 text-sm"><SelectValue placeholder="Choose condition preset…" /></SelectTrigger>
              <SelectContent>{tpl.condPresets.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex gap-2 items-start mb-3">
              <Textarea value={v.notes || ''} onChange={(e) => setItem(item, { notes: e.target.value })} rows={2} placeholder="Enter condition notes…" className="text-sm" />
              <MicButton onText={(t) => setItem(item, { notes: ((getItem(item).notes || '') + ' ' + t).trim() })} />
            </div>
          </>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {(v.photos || []).map((p, i) => <img key={i} src={p} alt="evidence" className="h-14 w-14 rounded-lg object-cover border border-border" />)}
          <label className="h-14 w-14 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-[#1c63c4] text-slate-500">
            {identifying === item ? <Icons.Loader2 className="h-5 w-5 animate-spin" /> : <Icons.Camera className="h-5 w-5" />}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files[0] && onPhoto(item, e.target.files[0])} />
          </label>
          {identifying === item && <span className="text-xs text-muted-foreground">AI identifying…</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex justify-center overflow-y-auto">
      <div className="bg-secondary/30 w-full max-w-3xl my-0 sm:my-6 sm:rounded-2xl min-h-screen sm:min-h-0">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-border sm:rounded-t-2xl">
          <div className="px-5 py-3 flex items-center justify-between">
            <div className="min-w-0"><div className="font-display font-bold text-[#102d55] truncate">Inventory & Condition</div><div className="text-xs text-muted-foreground truncate">{job.service_name} · {job.property_address}</div></div>
            <div className="flex items-center gap-3"><span className="text-xs font-semibold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full">{totalRecorded}/{totalItems}</span><button data-testid="recorder-close" onClick={onClose}><Icons.X className="h-5 w-5 text-slate-500" /></button></div>
          </div>
          {/* Step tabs */}
          <div className="flex px-2">
            {STEPS.map((s) => {
              const Ic = Icons[s.icon];
              const activeStep = step === s.id;
              return (
                <button key={s.id} data-testid={`step-${s.id}`} onClick={() => setStep(s.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeStep ? 'border-[#1c63c4] text-[#1c63c4]' : 'border-transparent text-slate-500 hover:text-[#102d55]'}`}>
                  <Ic className="h-4 w-4" /> <span className="hidden sm:inline">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {/* PROPERTY */}
          {step === 'property' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-border p-4">
                <div className="font-display font-semibold text-[#102d55] mb-2">Job details</div>
                <Field label="Service" value={job.service_name} />
                <Field label="Address" value={job.property_address} />
                <Field label="Property size" value={job.property_size} />
                <Field label="Scheduled date" value={job.scheduled_date} />
              </div>
              <div className="bg-white rounded-xl border border-border p-4 space-y-3">
                <div className="font-display font-semibold text-[#102d55]">Inspection details</div>
                <div><label className="text-sm text-muted-foreground">Inspector name</label>
                  <div className="flex gap-2 items-center mt-1">
                    <Input data-testid="prop-inspector" value={meta.inspector} onChange={(e) => setMetaField('inspector', e.target.value)} placeholder="e.g. James Carter" />
                    <MicButton onText={(t) => setMetaField('inspector', ((meta.inspector || '') + ' ' + t).trim())} />
                  </div>
                </div>
                <div><label className="text-sm text-muted-foreground">Inspection date</label><Input data-testid="prop-date" type="date" value={meta.date} onChange={(e) => setMetaField('date', e.target.value)} className="mt-1" /></div>
                <div><label className="text-sm text-muted-foreground">Tenant name</label>
                  <div className="flex gap-2 items-center mt-1">
                    <Input data-testid="prop-tenant" value={meta.tenant} onChange={(e) => setMetaField('tenant', e.target.value)} placeholder="Tenant (if applicable)" />
                    <MicButton onText={(t) => setMetaField('tenant', ((meta.tenant || '') + ' ' + t).trim())} />
                  </div>
                </div>
                <div><label className="text-sm text-muted-foreground">General property notes</label>
                  <div className="flex gap-2 items-start mt-1">
                    <Textarea data-testid="prop-notes" value={meta.notes} onChange={(e) => setMetaField('notes', e.target.value)} rows={3} placeholder="Overall condition, access notes, etc." className="text-sm" />
                    <MicButton onText={(t) => setMetaField('notes', ((meta.notes || '') + ' ' + t).trim())} />
                  </div>
                </div>
              </div>
              <Button data-testid="property-continue" onClick={() => setStep('rooms')} className="w-full bg-[#102d55] hover:bg-[#0b2244]">Continue to rooms <Icons.ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            </div>
          )}

          {/* ROOMS */}
          {step === 'rooms' && (
            <>
              <p className="text-sm text-muted-foreground mb-4">Select a room to begin recording. Each room lists the items you'll inspect.</p>
              <div className="space-y-3">
                {tpl.rooms.map((r) => {
                  const rec = recordedIn(r);
                  const complete = rec === r.items.length && rec > 0;
                  return (
                    <button key={r.id} data-testid={`room-card-${r.id}`} onClick={() => gotoRoom(r.id)}
                      className={`w-full text-left bg-white rounded-xl border p-4 transition-colors hover:border-[#1c63c4] ${complete ? 'border-emerald-300' : 'border-border'}`}>
                      <div className="flex items-center justify-between gap-3 mb-1.5">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="shrink-0 h-9 w-9 rounded-lg bg-[#eaf1fb] text-[#1c63c4] flex items-center justify-center"><Icons.ClipboardList className="h-5 w-5" /></span>
                          <span className="font-display font-semibold text-[#102d55] truncate">{r.name}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${complete ? 'bg-emerald-100 text-emerald-700' : rec > 0 ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{rec} / {r.items.length}</span>
                          <Icons.ChevronRight className="h-5 w-5 text-slate-400" />
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground leading-relaxed pl-[46px]">{r.items.join(' · ')}</div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* RECORD */}
          {step === 'record' && roomObj && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <Select value={room} onValueChange={setRoom}>
                  <SelectTrigger data-testid="record-room-select" className="bg-white flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-80">{tpl.rooms.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({recordedIn(r)}/{r.items.length})</SelectItem>)}</SelectContent>
                </Select>
                <span className="text-xs font-semibold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full shrink-0">{roomRecorded} / {roomObj.items.length}</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">Record condition for each item. Use the mic to dictate, or add a photo for AI identification.</p>
              <div className="space-y-4">{roomObj.items.map((item) => renderItemCard(item))}</div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setStep('rooms')} className="flex-1"><Icons.LayoutGrid className="h-4 w-4 mr-1.5" /> All rooms</Button>
                <Button onClick={() => setStep('review')} className="flex-1 bg-[#102d55] hover:bg-[#0b2244]">Review <Icons.ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              </div>
            </>
          )}

          {/* REVIEW */}
          {step === 'review' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-border p-4 flex items-center justify-between">
                <div><div className="font-display font-semibold text-[#102d55]">Ready to submit</div><div className="text-sm text-muted-foreground">Review the recorded items below.</div></div>
                <span className="text-sm font-bold bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full">{totalRecorded} / {totalItems}</span>
              </div>
              {(meta.inspector || meta.tenant || meta.notes) && (
                <div className="bg-white rounded-xl border border-border p-4">
                  <div className="font-display font-semibold text-[#102d55] mb-2">Inspection details</div>
                  <Field label="Inspector" value={meta.inspector} />
                  <Field label="Date" value={meta.date} />
                  <Field label="Tenant" value={meta.tenant} />
                  {meta.notes && <div className="pt-2 text-sm text-slate-600">{meta.notes}</div>}
                </div>
              )}
              {tpl.rooms.map((r) => {
                const items = r.items.map((it) => ({ name: it, ...(data[`${r.id}|${it}`] || {}) })).filter((i) => i.condition || i.description || i.notes || (i.photos && i.photos.length));
                if (!items.length) return null;
                return (
                  <div key={r.id} data-testid={`review-room-${r.id}`} className="bg-white rounded-xl border border-border p-4">
                    <div className="flex items-center justify-between mb-2"><div className="font-display font-semibold text-[#102d55]">{r.name}</div><button onClick={() => gotoRoom(r.id)} className="text-xs font-medium text-[#1c63c4] hover:underline">Edit</button></div>
                    <div className="divide-y divide-border">
                      {items.map((i) => (
                        <div key={i.name} className="py-2 flex items-start justify-between gap-3">
                          <div className="min-w-0"><div className="text-sm font-medium text-[#102d55]">{i.name}</div>{(i.description || i.notes) && <div className="text-xs text-muted-foreground">{[i.description, i.notes].filter(Boolean).join(' · ')}</div>}</div>
                          {i.condition && <span className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${CONDITION_COLORS[i.condition]}`}>{i.condition}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {totalRecorded === 0 && <p className="text-sm text-muted-foreground text-center py-6">Nothing recorded yet. Go to the Record step to add items.</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-border px-4 sm:px-5 py-3 flex gap-3 sm:rounded-b-2xl">
          <Button data-testid="save-close" variant="outline" onClick={onClose} className="flex-1">Save & close</Button>
          <Button data-testid="submit-report" onClick={submit} disabled={busy} className="flex-1 bg-[#102d55] hover:bg-[#0b2244]"><Icons.FileCheck2 className="h-4 w-4 mr-1.5" /> {busy ? 'Submitting…' : 'Submit & generate report'}</Button>
        </div>
      </div>
    </div>
  );
};

export default InventoryRecorder;
